import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Warehouse, Plus, Search, Edit, Trash2, Eye, X, CheckCircle2, XCircle, Package, MapPin, User, Phone } from 'lucide-react';

interface WarehouseItem {
  id: number;
  name: string;
  code: string;
  address?: string;
  managerName?: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
}

const T = {
  title: "المخازن",
  subtitle: "إدارة المخازن ومواقع التخزين",
  createNew: "إضافة مخزن جديد",
  totalWarehouses: "إجمالي المخازن",
  activeWarehouses: "المخازن النشطة",
  inactiveWarehouses: "المخازن غير النشطة",
  search: "بحث باسم المخزن أو الرمز...",
  loading: "جاري التحميل...",
  noWarehouses: "لا توجد مخازن",
  thName: "اسم المخزن",
  thCode: "الرمز",
  thAddress: "العنوان",
  thManager: "المسؤول",
  thPhone: "الهاتف",
  thStatus: "الحالة",
  thActions: "الإجراءات",
  view: "عرض",
  edit: "تعديل",
  delete: "حذف",
  editWarehouse: "تعديل المخزن",
  createWarehouse: "إضافة مخزن جديد",
  viewWarehouse: "تفاصيل المخزن",
  lblName: "اسم المخزن",
  lblCode: "رمز المخزن",
  lblAddress: "العنوان",
  lblManager: "اسم المسؤول",
  lblPhone: "رقم الهاتف",
  cancel: "إلغاء",
  update: "تحديث",
  create: "إضافة",
  close: "إغلاق",
  confirmDelete: "هل أنت متأكد من حذف هذا المخزن؟",
  updated: "تم تحديث المخزن بنجاح",
  created: "تم إضافة المخزن بنجاح",
  deleted: "تم حذف المخزن بنجاح",
  error: "حدث خطأ",
  active: "نشط",
  inactive: "غير نشط"
};

export default function WarehousesPage() {
  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<WarehouseItem | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseItem | null>(null);
  const [form, setForm] = useState({
    name: '', code: '', address: '', managerName: '', phone: '', isActive: true
  });

  useEffect(() => { loadWarehouses(); }, []);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/warehouses?pageSize=100');
      if (res.data?.success) {
        const data = res.data.data;
        setWarehouses(Array.isArray(data) ? data : (data.items || []));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingWarehouse(null);
    setForm({ name: '', code: '', address: '', managerName: '', phone: '', isActive: true });
    setShowModal(true);
  };

  const openEdit = (w: WarehouseItem) => {
    setEditingWarehouse(w);
    setForm({
      name: w.name || '',
      code: w.code || '',
      address: w.address || '',
      managerName: w.managerName || '',
      phone: w.phone || '',
      isActive: w.isActive
    });
    setShowModal(true);
  };

  const openView = (w: WarehouseItem) => { setSelectedWarehouse(w); setShowViewModal(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...form, id: editingWarehouse?.id };
      if (editingWarehouse) {
        await api.put('/api/warehouses/' + editingWarehouse.id, payload);
        alert(T.updated);
      } else {
        await api.post('/api/warehouses', payload);
        alert(T.created);
      }
      setShowModal(false);
      loadWarehouses();
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      await api.delete('/api/warehouses/' + id);
      loadWarehouses();
      alert(T.deleted);
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const filtered = warehouses.filter(w =>
    (w.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (w.code || '').toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = warehouses.filter(w => w.isActive).length;
  const inactiveCount = warehouses.filter(w => !w.isActive).length;
  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-amber-500 transition';

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
            <Warehouse className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>{T.createNew}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><Warehouse className="w-5 h-5 text-amber-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalWarehouses}</p>
          <p className="text-2xl font-bold text-gray-900">{warehouses.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">{T.activeWarehouses}</p>
          <p className="text-2xl font-bold text-green-700">{activeCount}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><XCircle className="w-5 h-5 text-red-600" /></div></div>
          <p className="text-sm text-gray-500">{T.inactiveWarehouses}</p>
          <p className="text-2xl font-bold text-red-700">{inactiveCount}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder={T.search} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-amber-500 transition" />
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div><p className="text-gray-500 mt-4">{T.loading}</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><Warehouse className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">{T.noWarehouses}</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thName}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thCode}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thAddress}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thManager}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thPhone}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thStatus}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((warehouse) => (
                    <tr key={warehouse.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white"><Warehouse className="w-5 h-5" /></div>
                          <p className="font-semibold text-gray-900">{warehouse.name}</p>
                        </div>
                      </td>
                      <td className="py-4 px-5"><span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold font-mono">{warehouse.code}</span></td>
                      <td className="py-4 px-5"><span className="text-gray-700 text-sm">{warehouse.address || '-'}</span></td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-700 text-sm">{warehouse.managerName || '-'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-700 text-sm font-mono">{warehouse.phone || '-'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${warehouse.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {warehouse.isActive ? T.active : T.inactive}
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openView(warehouse)} className="p-2 hover:bg-amber-50 rounded-lg text-gray-500 hover:text-amber-600 transition" title={T.view}><Eye className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(warehouse)} className="p-2 hover:bg-amber-50 rounded-lg text-gray-500 hover:text-amber-600 transition" title={T.edit}><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(warehouse.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title={T.delete}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingWarehouse ? T.editWarehouse : T.createWarehouse}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{T.lblName} *</Label><Input value={form.name} onChange={(e: any) => setForm({...form, name: e.target.value})} required className={inputClass} /></div>
                <div><Label>{T.lblCode} *</Label><Input value={form.code} onChange={(e: any) => setForm({...form, code: e.target.value})} required className={inputClass} /></div>
                <div className="md:col-span-2"><Label>{T.lblAddress}</Label><textarea value={form.address} onChange={(e: any) => setForm({...form, address: e.target.value})} className={inputClass} rows={2} /></div>
                <div><Label>{T.lblManager}</Label><Input value={form.managerName} onChange={(e: any) => setForm({...form, managerName: e.target.value})} className={inputClass} /></div>
                <div><Label>{T.lblPhone}</Label><Input value={form.phone} onChange={(e: any) => setForm({...form, phone: e.target.value})} className={inputClass} /></div>
                <div className="md:col-span-2 flex items-center gap-2"><input type="checkbox" id="whActive" checked={form.isActive} onChange={(e) => setForm({...form, isActive: e.target.checked})} className="w-4 h-4" /><label htmlFor="whActive" className="text-sm font-medium">{T.active}</label></div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">{T.cancel}</Button>
                <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">{editingWarehouse ? T.update : T.create}</Button>
              </div>
            </form>
          </div></Card>
        </div>
      )}

      {showViewModal && selectedWarehouse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{T.viewWarehouse}</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-4 pb-4 border-b mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white"><Warehouse className="w-8 h-8" /></div>
              <div><h3 className="text-xl font-bold">{selectedWarehouse.name}</h3><p className="text-gray-500 font-mono">{selectedWarehouse.code}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><p className="text-sm text-gray-500">{T.thAddress}</p><p className="font-semibold">{selectedWarehouse.address || '-'}</p></div>
              <div><p className="text-sm text-gray-500">{T.thManager}</p><p className="font-semibold">{selectedWarehouse.managerName || '-'}</p></div>
              <div><p className="text-sm text-gray-500">{T.thPhone}</p><p className="font-semibold font-mono">{selectedWarehouse.phone || '-'}</p></div>
              <div><p className="text-sm text-gray-500">{T.thStatus}</p><span className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedWarehouse.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{selectedWarehouse.isActive ? T.active : T.inactive}</span></div>
              <div><p className="text-sm text-gray-500">تاريخ الإنشاء</p><p className="font-semibold">{new Date(selectedWarehouse.createdAt).toLocaleString('ar-EG')}</p></div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => { setShowViewModal(false); openEdit(selectedWarehouse); }} className="flex-1 bg-amber-600 hover:bg-amber-700"><Edit className="w-4 h-4 ml-2" />{T.edit}</Button>
              <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">{T.close}</Button>
            </div>
          </div></Card>
        </div>
      )}
    </div>
  );
}
